// ChatbotGate.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "./context/UserContext";
import { Navigate } from "react-router-dom";
import { CHATBOT_API_BASE_URL } from "./config/apiConfig";

const HOST_ID = "salesplay-chatbot";
// const CHATBOT_BASE_URL = "https://chat.mnmks.com"; // "https://stagechat.mnmks.com", "https://chat.mnmks.com", "http://localhost:5173/chatbot/"
const CHATBOT_BASE_URL = CHATBOT_API_BASE_URL;
const CHATBOT_VERSION_KEY = "__chatbot_loaded_version";

async function fetchChatbotVersion() {
  return new Promise((resolve) => {
    // Remove old version script if exists
    const existing = document.querySelector('script[data-chatbot-version="1"]');
    if (existing) existing.remove();

    // Clear old global
    delete window.__CHATBOT_VERSION;

    const script = document.createElement("script");
    script.src = `${CHATBOT_BASE_URL}/version.js?_=${Date.now()}`;
    script.dataset.chatbotVersion = "1";
    script.onload = () => {
      const version = window.__CHATBOT_VERSION;
      console.log("[ChatbotGate] Loaded version:", version);
      resolve(version || null);
    };
    script.onerror = () => {
      console.log("[ChatbotGate] version.js not found, using fallback");
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

function loadScriptWithVersion(src, version) {
  const versionedSrc = version ? `${src}?v=${version}` : src;

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-chatbot-iife="1"]');
    if (existing) {
      existing.remove();
    }

    const s = document.createElement("script");
    s.src = versionedSrc;
    s.async = true;
    s.dataset.chatbotIife = "1";
    s.dataset.version = version || "";
    s.addEventListener("load", resolve, { once: true });
    s.addEventListener("error", reject, { once: true });
    document.head.appendChild(s);
    console.log("[ChatbotGate] Loading script:", versionedSrc);
  });
}

function loadCssWithVersion(href, version) {
  const versionedHref = version ? `${href}?v=${version}` : href;

  return new Promise((resolve) => {
    const existing = document.querySelector('link[data-chatbot-css="1"]');
    if (existing) {
      existing.remove();
    }

    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = versionedHref;
    l.dataset.chatbotCss = "1";
    l.dataset.version = version || "";
    l.addEventListener("load", resolve, { once: true });
    l.addEventListener("error", resolve, { once: true });
    document.head.insertBefore(l, document.head.firstChild);
    console.log("[ChatbotGate] Loading CSS:", versionedHref);
  });
}

function buildDisplayName(user) {
  const parts = [user?.firstname, user?.lastname]
    .map((s) => (s || "").trim())
    .filter(Boolean);
  if (parts.length) return parts.join(" ").replace(/\s+/g, " ").trim();
  if (user?.name?.trim()) return user.name.trim();
  const email = user?.email || user?.mail;
  if (email && email.includes("@")) return email.split("@")[0];
  return "";
}

export default function ChatbotGate({ token, height = "calc(100vh - 64px)" }) {
  const { user, loading } = useUser();
  const displayName = useMemo(() => buildDisplayName(user), [user]);

  const accessToken = localStorage.getItem("access_token");
  console.log(accessToken);

  const hostRef = useRef(null);
  const prevNameRef = useRef("");
  const prevTokenRef = useRef("");
  const prevUserIdRef = useRef(null);
  const versionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (loading) return;

    (async () => {
      const path = (window.location && window.location.pathname) || "";
      if (!/^\/watchboard(\/|$)/.test(path)) {
        try {
          window.__chatbot?.destroy?.();
        } catch {}
        return;
      }

      const currentVersion = await fetchChatbotVersion();
      if (cancelled) return;

      const loadedVersion = localStorage.getItem(CHATBOT_VERSION_KEY);
      if (loadedVersion && currentVersion && loadedVersion !== currentVersion) {
        console.log(
          `[ChatbotGate] Version changed: ${loadedVersion} -> ${currentVersion}, reloading...`
        );
        try {
          window.__chatbot?.destroy?.();
        } catch {}
        window.__chatbotMounted = false;
        delete window.initChatbot;
      }

      if (currentVersion) {
        localStorage.setItem(CHATBOT_VERSION_KEY, currentVersion);
        versionRef.current = currentVersion;
      }

      try {
        await Promise.all([
          loadScriptWithVersion(
            `${CHATBOT_BASE_URL}/chatbot.iife.js`,
            currentVersion
          ),
          loadCssWithVersion(
            `${CHATBOT_BASE_URL}/mnm_chat_bot.css`,
            currentVersion
          ),
        ]);
      } catch (err) {
        console.error("[ChatbotGate] Failed to load assets:", err);
        return;
      }
      if (cancelled) return;

      for (let i = 0; i < 20 && typeof window.initChatbot !== "function"; i++) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (typeof window.initChatbot !== "function" || cancelled) return;

      const host = hostRef.current;
      if (!host) return;
      if (!host.id) host.id = HOST_ID;

      const nameForWidget = displayName || "Guest";
      const currentUserId = user?.id || user?._id || user?.userId || null;
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      if (!window.__chatbotMounted) {
        try {
          window.__chatbot = window.initChatbot({
            id: host.id,
            cssUrl: versionRef.current
              ? `${CHATBOT_BASE_URL}/mnm_chat_bot.css?v=${versionRef.current}`
              : `${CHATBOT_BASE_URL}/mnm_chat_bot.css`,
            title: "SalesPlay Assistant",
            theme: "light",
            position: "right",
            v2Enable: true,
            ksAuthToken: accessToken,
            userName: nameForWidget,
            userId: currentUserId,
            user: user,
            userCompanyName: user?.company_name || "",
            ...(headers ? { headers } : {}),
          });
        } catch (err) {
          return;
        }
        window.__chatbotMounted = true;
        prevNameRef.current = nameForWidget;
        prevTokenRef.current = token || "";
        prevUserIdRef.current = currentUserId;
        return;
      }

      if (nameForWidget && nameForWidget !== prevNameRef.current) {
        if (window.__chatbot?.setUserName) {
          window.__chatbot.setUserName(nameForWidget);
        } else if (!window.__chatbotRemountedOnce) {
          window.__chatbotRemountedOnce = true;
          try {
            window.__chatbot?.destroy?.();
          } catch {}
          window.__chatbot = window.initChatbot({
            id: host.id,
            cssUrl: versionRef.current
              ? `${CHATBOT_BASE_URL}/mnm_chat_bot.css?v=${versionRef.current}`
              : `${CHATBOT_BASE_URL}/mnm_chat_bot.css`,
            title: "SalesPlay Assistant",
            theme: "light",
            position: "right",
            v2Enable: true,
            userName: nameForWidget,
            userId: currentUserId,
            user: user,
            userCompanyName: user?.company_name || "",
            ...(headers ? { headers } : {}),
          });
        }
        prevNameRef.current = nameForWidget;
      }

      if (currentUserId !== prevUserIdRef.current) {
        if (window.__chatbot?.setUserId) {
          window.__chatbot.setUserId(currentUserId);
        }
        prevUserIdRef.current = currentUserId;
      }

      if ((token || "") !== prevTokenRef.current) {
        if (window.__chatbot?.setAuth) window.__chatbot.setAuth(headers || {});
        else if (window.__chatbot?.setHeaders)
          window.__chatbot.setHeaders(headers || {});
        prevTokenRef.current = token || "";
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, displayName, user, accessToken, loading]);

  useEffect(() => {
    return () => {
      try {
        window.__chatbot?.destroy?.();
      } catch {}
      window.__chatbotMounted = false;
    };
  }, []);

  // Periodic version check every 30 seconds
  useEffect(() => {
    const path = (window.location && window.location.pathname) || "";
    if (!/^\/watchboard(\/|$)/.test(path)) return;

    const checkVersion = async () => {
      const newVersion = await fetchChatbotVersion();
      if (!newVersion) return;

      const loadedVersion = versionRef.current;
      if (loadedVersion && loadedVersion !== newVersion) {
        console.log(
          `[ChatbotGate] New version detected: ${loadedVersion} -> ${newVersion}, reloading...`
        );

        try {
          window.__chatbot?.destroy?.();
        } catch {}
        window.__chatbotMounted = false;
        delete window.initChatbot;

        versionRef.current = newVersion;
        localStorage.setItem(CHATBOT_VERSION_KEY, newVersion);

        // Reload assets with new version
        await Promise.all([
          loadScriptWithVersion(
            `${CHATBOT_BASE_URL}/chatbot.iife.js`,
            newVersion
          ),
          loadCssWithVersion(
            `${CHATBOT_BASE_URL}/mnm_chat_bot.css`,
            newVersion
          ),
        ]);

        // Wait for initChatbot
        for (
          let i = 0;
          i < 20 && typeof window.initChatbot !== "function";
          i++
        ) {
          await new Promise((r) => setTimeout(r, 50));
        }

        // Reinitialize chatbot
        if (typeof window.initChatbot === "function" && hostRef.current) {
          const nameForWidget = displayName || "Guest";
          const currentUserId = user?.id || user?._id || user?.userId || null;
          const headers = token
            ? { Authorization: `Bearer ${token}` }
            : undefined;

          window.__chatbot = window.initChatbot({
            id: hostRef.current.id || HOST_ID,
            cssUrl: `${CHATBOT_BASE_URL}/mnm_chat_bot.css?v=${newVersion}`,
            title: "SalesPlay Assistant",
            theme: "light",
            position: "right",
            v2Enable: true,
            ksAuthToken: accessToken,
            userName: nameForWidget,
            userId: currentUserId,
            user: user,
            userCompanyName: user?.company_name || "",
            ...(headers ? { headers } : {}),
          });
          window.__chatbotMounted = true;
          console.log(
            "[ChatbotGate] Chatbot reloaded with new version:",
            newVersion
          );
        }
      }
    };

    const intervalId = setInterval(checkVersion, 30000);
    return () => clearInterval(intervalId);
  }, [displayName, user, token, accessToken]);

  if (loading) return null;

  // Require authenticated user to render chatbot (secondary check)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <div ref={hostRef} id={HOST_ID} style={{ width: "100%", height }} />;
}
